import { Row } from "react-bootstrap";
import savedSearchIconV2 from "src/imagesV2/savedSearchIconV2.svg";
import Image from "react-bootstrap/Image";
import { useHistory } from "react-router-dom";
import { SAVED_SEARCHES_PAGE } from "src/router/routes";
import services from "src/components/common/MainContent/SavedSearches/Services/Api/SaveSearchService";
import { connect, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import {
  setToggleRelatedSpecialties,
  clearSpecsAndRelatedSpecsMapList,
} from "src/redux/globalSearch/action";
import jobSearchQueryBuilder from "src/services/QueryBuilder/JobSearchQueryBuilder";
import { useTranslation, withTranslation } from "react-i18next";
import clsx from "clsx";
import isEmpty from "lodash/isEmpty";
import Analytics from "src/services/Analytics";
import { Grid, Skeleton, useMediaQuery } from "@mui/material";
import { sortSavedSearchesByDateCreated } from "src/utils/sortSavedSearches";
import styles from "./SavedSearchHome.module.scss";

interface SavedSearchHomeProps {
  isProfileComplete: boolean;
  setToggleRelatedSpecs: (payload: boolean) => void;
  clearRelatedSpecialties: () => void;
}

interface RootState {
  auth: {
    candidateId: number;
  };
}

const SavedSearchHome = ({
  isProfileComplete,
  setToggleRelatedSpecs,
  clearRelatedSpecialties,
}: SavedSearchHomeProps) => {
  const history = useHistory();
  const { t } = useTranslation();
  const candidateId = useSelector((state: RootState) => state.auth.candidateId);
  const [savedSearches, setSavedSearches] = useState({});
  const [showSeeAll, setShowSeeAll] = useState(false);
  const [renderSavedSearch, setRenderSavedSearch] = useState(false);
  const savedSearchList = Array.isArray(savedSearches) ? savedSearches : Object.values(savedSearches);
  const [isSaveSearchLoading, setIsSaveSearchLoading] = useState(true);
  const isCompact = useMediaQuery("(max-width: 768px)");

  const getSavedSearches = () => {
    services.getAll(candidateId, "savedSearches").then((savedSearchData) => {
      const sortedSavedSearches: any = sortSavedSearchesByDateCreated(savedSearchData.savedSearches)
      setSavedSearches(sortedSavedSearches || null);
      setIsSaveSearchLoading(false);
    });
  };

  useEffect(() => {
    if (savedSearchList.length > 4) 
      setShowSeeAll(true);
    if (savedSearchList.length > 0) 
      setRenderSavedSearch(true);
  }, [savedSearchList]);

  useEffect(() => {
    if (candidateId) {
      getSavedSearches();
    }
  }, [candidateId]);

  const saveSearchNavigation = () => {
    history.push(SAVED_SEARCHES_PAGE);
  };
  
  const viewJob = (result) => {
    let relatedSpecialtiesArray: any[] = [];
    if(result?.searchCriteria?.specialtyMapping){
      relatedSpecialtiesArray = Object.values(result.searchCriteria.specialtyMapping)?.flat();
    }
    if (relatedSpecialtiesArray.length === 0) {
      clearRelatedSpecialties();
      setToggleRelatedSpecs(false);
    } else {
      setToggleRelatedSpecs(true);
    }
    if (result && !isEmpty(result.searchCriteria)) {
      const searchCriteria = {
        location: JSON.stringify(result.searchCriteria.location),
        specialty: JSON.stringify(result.searchCriteria.specialty),
        geoLocationRadius: result.searchCriteria.geoLocationRadius,
        startDate: result.searchCriteria.startDate,
        shiftTypes: result.searchCriteria.shiftTypes,
        refreshPayFilter: "true",
        shiftLengths: result.searchCriteria.shiftLengths,
        durations: result.searchCriteria.durations,
        salaryMax: result.searchCriteria.salaryMax,
        sort: result.searchCriteria.sort,
        priority: result.searchCriteria.priority,
        jobDetails: result.searchCriteria.jobDetails,
        datePosted: result.searchCriteria.datePosted,
        shouldFilterByQuickApplyEligibility:
          result?.searchCriteria?.shouldFilterByQuickApplyEligibility?.toString() ?? "",
        relatedSpecialties: result?.searchCriteria?.relatedSpecialties,
        specsWithRelSpecs: result?.searchCriteria?.specialtyMapping
          ? JSON.stringify(result.searchCriteria.specialtyMapping)
          : "",
      };

      jobSearchQueryBuilder.handleParamsChange(searchCriteria);

      const segmentEventName = `${t("segment.jobSearched")}`;
      const segmentEventProps = {
        segment_source_name: `${process.env.NODE_ENV}-web-gateway`,
        origin: document.referrer,
        sourceType: `${t("segment.homepage")}`,
        isSavedSearch: true,
        ...searchCriteria,
      };

      Analytics.segmentTrackEvent(segmentEventName, segmentEventProps);
    }
  };

  return (
    <>
      {isSaveSearchLoading ? (
        <div>
          <div className={styles.skeletonSaveSearchTxt}>
            <Skeleton />
          </div>
          {!isCompact ? (
            <Grid container className={styles.skeletonSaveSearchChip}>
              <Skeleton />
              <Skeleton />
              <Skeleton />
              <Skeleton />
            </Grid>
          ) : (
            <Grid container className={styles.skeletonSaveSearchChip}>
              <Skeleton />
            </Grid>
          )}
        </div>
      ) : (
        <div>
          {renderSavedSearch && (
            <Row className={styles.savedSearchesChipContainer}>
              <Row className={styles.saveSearchIcontxt}>
                <div>
                  <Image src={savedSearchIconV2} className={styles.savedSearchIconV2} />
                </div>
                <div
                  className={clsx(
                    !isProfileComplete && styles.incompleteprofileSavedSearchTxt,
                    styles.savedSearchTxt
                  )}
                >
                  {t("dashboard.savedSearches.yourSavedSearchesTxt")}
                </div>
                {showSeeAll && (
                  <button
                    type="button"
                    className={
                      isProfileComplete
                        ? styles.seeAllTxt
                        : `${styles.seeAllTxtIncompleteProfile} ${styles.seeAllTxt}`
                    }
                    onClick={saveSearchNavigation}
                  >
                    {t("dashboard.savedSearches.seeAll")}
                  </button>
                )}
              </Row>
              <Row className={styles.savedSearchChipRow}>
                {savedSearchList.slice(0, 4).map((search) => (
                  <button
                    key={search.id || search.name}
                    type="button"
                    className={styles.savedSearchChip}
                    onClick={() => viewJob(search)}
                  >
                    <span className={styles.chipText}>
                      {search?.name.length > 40 ? `${search.name.slice(0, 40)}…` : search.name}
                    </span>
                  </button>
                ))}
              </Row>
            </Row>
          )}
        </div>
      )}
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  candidateId: state.auth.candidateId,
});

const mapDispatchToProps  = (dispatch) => ({
  setToggleRelatedSpecs: (payload) => dispatch(setToggleRelatedSpecialties(payload)),
  clearRelatedSpecialties: () => dispatch(clearSpecsAndRelatedSpecsMapList()),
});

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(SavedSearchHome));